import { parseAttributeTypeCodes } from './useWellTestOverviewColumns';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, isApiError } from '../../shared/hooks/useApi';
import { useSettingsStore } from '../store/settings-store';
// Mirrors SETTINGS_ALLOWED_FIELD_SOURCE_PATTERN in
// src/modules/well_test_management.api.ts. Advisory only — the
// server-side copy is what actually gates the write; this exists so an
// invalid Field source shows an inline row error before Save instead of
// only after the server rejects it. Keep both in sync if either changes;
// see that file's comment above its pattern for the full "no allow-list"
// trade-off (WI 36058 AC requires Field sources to be free text; this
// endpoint is admin-only, so table-prefix + identifier-shape validation
// is used instead of an enumerated list).
export const FIELD_SOURCE_PATTERN = /^(?:stops|routes|forms)\.[a-z_][a-z0-9_]*$/;
function sameRows(left, right) {
    if (left.length !== right.length) {
        return false;
    }
    return left.every((row, index) => {
        const saved = right[index];
        return row.name === saved.name
            && row.type === saved.type
            && row.sourceOrCodesText === saved.sourceOrCodesText
            && row.unit === saved.unit
            && row.columnSizeText === saved.columnSizeText
            && row.columnAlignment === saved.columnAlignment
            && row.calculated === saved.calculated
            && row.invisible === saved.invisible
            && row.headerSearchable === saved.headerSearchable;
    });
}
function emptyRow(id = crypto.randomUUID()) {
    return {
        id,
        name: '',
        type: 'attribute',
        sourceOrCodesText: '',
        unit: null,
        columnSizeText: '',
        columnAlignment: null,
        calculated: false,
        invisible: false,
        headerSearchable: false,
    };
}
function toRow(column) {
    var _a, _b;
    const isAttribute = Array.isArray(column.attribute_type_code);
    return {
        id: crypto.randomUUID(),
        name: column.name,
        type: isAttribute ? 'attribute' : 'field',
        sourceOrCodesText: isAttribute ? column.attribute_type_code.join(', ') : (_a = column.source) !== null && _a !== void 0 ? _a : '',
        unit: typeof column.unit === 'number' ? column.unit : null,
        columnSizeText: typeof column.column_size === 'number' ? String(column.column_size) : '',
        columnAlignment: (_b = column.column_alignment) !== null && _b !== void 0 ? _b : null,
        calculated: column.calculated === true,
        invisible: column.invisible === true,
        headerSearchable: column.header_searchable === true,
    };
}
function toWireRow(row) {
    return {
        name: row.name,
        type: row.type,
        sourceOrCodesText: row.sourceOrCodesText,
        unit: row.unit,
        column_size: row.columnSizeText.trim() === '' ? null : Number(row.columnSizeText.trim()),
        column_alignment: row.columnAlignment,
        calculated: row.calculated,
        invisible: row.invisible,
        header_searchable: row.headerSearchable,
    };
}
function errorMessage(error) {
    const fallback = 'An error occurred while saving.';
    if (!(error instanceof Error)) {
        return fallback;
    }
    try {
        const parsed = JSON.parse(error.message);
        const message = parsed !== null && typeof parsed === 'object' ? Reflect.get(parsed, 'message') : undefined;
        return typeof message === 'string' && message.trim() !== '' ? message : fallback;
    }
    catch (_a) {
        return /^\d{3}\s/.test(error.message) ? fallback : error.message || fallback;
    }
}
export function useWellTestManagementColumns(enabled) {
    const notify = useSettingsStore((state) => state.notify);
    const [rows, setRows] = useState([]);
    const [savedRows, setSavedRows] = useState([]);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(enabled);
    const [saving, setSaving] = useState(false);
    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    // True once a settings GET has actually returned the organization's stored
    // configuration (even an empty one) since this hook last mounted.
    // `rows.length === 0` alone cannot distinguish "the user has not touched
    // this editor and we've never confirmed what the server holds" (a fresh
    // page load with the feature off, or the 401 during the local-enable-
    // before-persist window) from "the user loaded the existing configuration
    // and intentionally cleared every row" -- only this flag can. See `save()`.
    const loadedRef = useRef(false);
    const load = useCallback(async () => {
        var _a, _b;
        setLoading(true);
        try {
            const response = await apiFetch('/api/well_test_management/settings');
            const nextRows = ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.columns) !== null && _b !== void 0 ? _b : []).map(toRow);
            setRows(nextRows);
            setSavedRows(nextRows);
            setErrors({});
            loadedRef.current = true;
            return nextRows;
        }
        catch (error) {
            // A 401 here means the organization's persisted feature_codes don't
            // (yet) include Well Tests + Well Test Management — most commonly the
            // transient window between ticking the checkbox locally and the
            // organization PUT that persists it (useSettingsForm's `enabled` is
            // derived from local, unsaved featureCodes). It says nothing about
            // whether a configuration exists, so it must never clear rows that
            // were already loaded or are mid-edit — doing so lets the next Update
            // PUT an empty columns list over a saved configuration (WI 36058).
            if (!(isApiError(error) && error.status === 401)) {
                notify('Error', 'Failed to load Well Test Management column settings.', 'error');
            }
            return undefined;
        }
        finally {
            setLoading(false);
        }
    }, [notify]);
    useEffect(() => {
        if (!enabled) {
            setLoading(false);
            return;
        }
        void load();
    }, [enabled, load]);
    const addRow = useCallback(() => {
        setRows((previous) => [...previous, emptyRow()]);
    }, []);
    const removeRow = useCallback((id) => {
        setRows((previous) => previous.filter((row) => row.id !== id));
        setErrors((previous) => {
            if (!(id in previous)) {
                return previous;
            }
            const next = { ...previous };
            delete next[id];
            return next;
        });
    }, []);
    const updateName = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, name: value } : row));
    }, []);
    const updateType = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, type: value } : row));
    }, []);
    const updateSourceOrCodesText = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, sourceOrCodesText: value } : row));
    }, []);
    const updateUnit = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, unit: value } : row));
    }, []);
    const updateColumnSizeText = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, columnSizeText: value } : row));
    }, []);
    const updateColumnAlignment = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, columnAlignment: value } : row));
    }, []);
    const updateCalculated = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, calculated: value } : row));
    }, []);
    const updateInvisible = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, invisible: value } : row));
    }, []);
    const updateHeaderSearchable = useCallback((id, value) => {
        setRows((previous) => previous.map((row) => row.id === id ? { ...row, headerSearchable: value } : row));
    }, []);
    const moveRow = useCallback((fromIndex, toIndex) => {
        setRows((previous) => {
            if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= previous.length || toIndex >= previous.length) {
                return previous;
            }
            const next = [...previous];
            const [moved] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, moved);
            return next;
        });
    }, []);
    const validate = useCallback((rowsToValidate = rows) => {
        const next = {};
        for (const row of rowsToValidate) {
            const rowErrors = {};
            if (!row.name.trim()) {
                rowErrors.name = 'Required';
            }
            if (!row.sourceOrCodesText.trim()) {
                rowErrors.sourceOrCodesText = 'Required';
            }
            else if (row.type === 'attribute' && parseAttributeTypeCodes(row.sourceOrCodesText) === null) {
                rowErrors.sourceOrCodesText = 'Enter digits separated by commas (e.g. 78,206 or 79, 201).';
            }
            else if (row.type === 'field' && !FIELD_SOURCE_PATTERN.test(row.sourceOrCodesText.trim())) {
                rowErrors.sourceOrCodesText = 'Source must be a column on stops, routes, or forms (e.g. stops.name).';
            }
            const size = row.columnSizeText.trim();
            if (!/^\d+$/.test(size) || Number(size) < 1) {
                rowErrors.columnSize = 'Must be a positive whole number.';
            }
            if (Object.keys(rowErrors).length > 0) {
                next[row.id] = rowErrors;
            }
        }
        setErrors(next);
        return Object.keys(next).length === 0;
    }, [rows]);
    const reset = useCallback(() => {
        if (enabledRef.current) {
            void load();
        }
    }, [load]);
    const save = useCallback(async () => {
        var _a, _b;
        if (!enabled) {
            return true;
        }
        setSaving(true);
        try {
            let rowsToSave = rows;
            if (!loadedRef.current && rows.length === 0) {
                // Never confirmed what the server holds, and nothing has been typed
                // locally either -- `rows` being empty is an artifact of a fresh
                // mount or a transient 401, not a user decision. By now
                // App.handleUpdate has already awaited the organization's feature
                // PUT, so this fetch is authorized and returns the durable
                // configuration; use it instead of overwriting with `[]`. If it
                // fails, do not fall back to the destructive empty write.
                const recovered = await load();
                if (recovered === undefined) {
                    notify('Error', 'Failed to load the existing Well Test Management column configuration. Columns were not updated.', 'error');
                    return false;
                }
                rowsToSave = recovered;
            }
            if (!validate(rowsToSave)) {
                return false;
            }
            const response = await apiFetch('/api/well_test_management/settings', {
                method: 'PUT',
                body: JSON.stringify({ columns: rowsToSave.map(toWireRow) }),
            });
            const nextRows = ((_b = (_a = response.data) === null || _a === void 0 ? void 0 : _a.columns) !== null && _b !== void 0 ? _b : []).map(toRow);
            setRows(nextRows);
            setSavedRows(nextRows);
            setErrors({});
            loadedRef.current = true;
            notify('Success', 'Update successful.', 'success');
            return true;
        }
        catch (error) {
            notify('Error', errorMessage(error), 'error');
            return false;
        }
        finally {
            setSaving(false);
        }
    }, [enabled, load, notify, rows, validate]);
    const hasUnsavedChanges = useMemo(() => !sameRows(rows, savedRows), [rows, savedRows]);
    return {
        rows,
        errors,
        loading,
        saving,
        enabled,
        hasUnsavedChanges,
        addRow,
        removeRow,
        updateName,
        updateType,
        updateSourceOrCodesText,
        updateUnit,
        updateColumnSizeText,
        updateColumnAlignment,
        updateCalculated,
        updateInvisible,
        updateHeaderSearchable,
        moveRow,
        validate,
        save,
        reset,
    };
}
